import type { Letter } from "@repo/hindi-shared";
import { letters, mulberry32, strokeOrderRules } from "@repo/hindi-shared";
import { useState } from "react";

import { createSpeechAudio } from "../audio";
import { dateKey, hashSeed } from "../exercises/seed";
import { navigate } from "../router";
import { setPendingPlan } from "../session/customPlan";
import { loadState } from "../state/store";
import { DualLabel } from "../ui/DualLabel";
import { GlyphTile } from "../ui/GlyphTile";
import { LetterDetail } from "../ui/LetterDetail";
import { RubricNumeral } from "../ui/RubricNumeral";
import { Shirorekha } from "../ui/Shirorekha";

// Varga group names are organizational HEADERS (chrome) over the consonant
// rows — a non-reader needs the English to know what each group is. English
// leads with a Devanagari echo, matching how the Lessons screen labels the
// same groups. (The consonants themselves, below each header, stay Devanagari
// — those are the content being taught.)
const VARGA_LABELS: Record<string, { en: string; hi: string }> = {
  "ka-varga": { en: "Ka-varga (velars)", hi: "क वर्ग" },
  "ca-varga": { en: "Ca-varga (palatals)", hi: "च वर्ग" },
  "ta-varga-retroflex": { en: "Ta-varga (retroflex)", hi: "ट वर्ग" },
  "ta-varga-dental": { en: "Ta-varga (dental)", hi: "त वर्ग" },
  "pa-varga": { en: "Pa-varga (labials)", hi: "प वर्ग" },
  antastha: { en: "Semivowels (antastha)", hi: "अन्तःस्थ" },
  ushma: { en: "Sibilants & ha (ushma)", hi: "ऊष्म" },
};

/** Canonical varga order for grouping consonant rows. */
const VARGA_ORDER = [
  "ka-varga",
  "ca-varga",
  "ta-varga-retroflex",
  "ta-varga-dental",
  "pa-varga",
  "antastha",
  "ushma",
];

interface Section {
  numeral: number;
  titleEn: string;
  titleHi: string;
  groups: { label: { en: string; hi: string } | null; letters: Letter[] }[];
}

function buildSections(): Section[] {
  const vowels = letters.filter((letter) => letter.kind === "vowel");
  const consonants = letters.filter((letter) => letter.kind === "consonant");
  const matras = letters.filter((letter) => letter.kind === "matra");
  const numerals = letters.filter((letter) => letter.kind === "numeral");
  const conjuncts = letters.filter(
    (letter) => letter.kind === "conjunct" || letter.kind === "nukta",
  );

  const consonantGroups = VARGA_ORDER.map((varga) => ({
    label: VARGA_LABELS[varga] ?? null,
    letters: consonants.filter((letter) => letter.varga === varga),
  })).filter((group) => group.letters.length > 0);

  return [
    {
      numeral: 1,
      titleEn: "Vowels",
      titleHi: "स्वर",
      groups: [{ label: null, letters: vowels }],
    },
    {
      numeral: 2,
      titleEn: "Consonants",
      titleHi: "व्यंजन",
      groups: consonantGroups,
    },
    {
      numeral: 3,
      titleEn: "Matras",
      titleHi: "मात्राएँ",
      groups: [{ label: null, letters: matras }],
    },
    {
      numeral: 4,
      titleEn: "Numerals",
      titleHi: "अंक",
      groups: [{ label: null, letters: numerals }],
    },
    {
      numeral: 5,
      titleEn: "Conjuncts",
      titleHi: "संयुक्ताक्षर",
      groups: [{ label: null, letters: conjuncts }],
    },
  ];
}

const SECTIONS = buildSections();

/** Mastery tier for a letter, from the learner's SRS review state. */
type Mastery = "unseen" | "learning" | "strong";

const STRONG_INTERVAL_DAYS = 21;

function masteryFor(
  letterId: string,
  reviews: Record<string, { reps: number; intervalDays: number }>,
): Mastery {
  const review = reviews[letterId];
  if (review === undefined || review.reps < 1) return "unseen";
  if (review.intervalDays >= STRONG_INTERVAL_DAYS) return "strong";
  return "learning";
}

const audio = createSpeechAudio(() => loadState().settings);

/**
 * Varnamala — the manuscript-page progress map: every taught Devanagari
 * item, grouped under Shirorekha section rules with RubricNumeral section
 * numbers, ink-density showing mastery (unseen faint, learning full ink,
 * strong with a gold underline dot). Tapping a letter opens `LetterDetail`.
 */
export function Varnamala() {
  const [openLetterId, setOpenLetterId] = useState<string | null>(null);
  const [rulesOpen, setRulesOpen] = useState(false);
  const store = loadState();
  const openLetter = letters.find((letter) => letter.id === openLetterId);
  // Fresh map — nothing reviewed yet, every tile still faint. The empty
  // state explains the ink-density language the map speaks from then on.
  const isFresh = Object.keys(store.reviews).length === 0;

  function handlePractice(letterId: string): void {
    const rand = mulberry32(hashSeed(`${letterId}:${dateKey()}`));
    const kind = pickPracticeKind(letterId, rand);
    setPendingPlan({
      newLessonId: null,
      blocks: [
        { kind: "review", exercises: [{ kind, itemId: letterId }] },
        { kind: "summary", exercises: [] },
      ],
    });
    setOpenLetterId(null);
    navigate("/session");
  }

  return (
    <main className="paper-grain min-h-dvh px-6 py-12">
      <section className="mx-auto w-full max-w-2xl">
        <Shirorekha className="pt-4">
          <button
            type="button"
            onClick={() => navigate("/")}
            aria-label="Home"
            className="inline-flex items-center gap-1.5 transition-colors duration-150 hover:text-sindoor"
          >
            <span aria-hidden="true" className="text-xs text-ink-soft">
              ←
            </span>
            <DualLabel
              en="Hindi"
              hi="हिन्दी"
              className="text-xs text-ink-soft uppercase"
            />
          </button>
          <h1 className="mt-4 font-display text-5xl leading-[0.95] text-ink">
            <DualLabel en="Varnamala" hi="वर्णमाला" />
          </h1>
          {isFresh && (
            <p className="mt-2 font-latin text-base text-ink-soft">
              <span lang="en">
                Every letter starts in faint ink. As you learn, the ink darkens.
              </span>
            </p>
          )}
        </Shirorekha>

        <div className="mt-10 flex flex-col gap-12">
          {SECTIONS.map((section) => (
            <div key={section.titleHi}>
              <Shirorekha>
                <p className="font-dev text-lg text-ink">
                  <RubricNumeral value={section.numeral} className="mr-2" />
                  <DualLabel en={section.titleEn} hi={section.titleHi} />
                </p>
              </Shirorekha>

              <div className="mt-4 flex flex-col gap-6">
                {section.groups.map((group, index) => (
                  <div key={group.label?.en ?? index}>
                    {group.label !== null && (
                      <DualLabel
                        en={group.label.en}
                        hi={group.label.hi}
                        className="mb-2 text-sm text-ink-soft"
                      />
                    )}
                    <div className="flex flex-wrap gap-2">
                      {group.letters.map((letter) => {
                        const mastery = masteryFor(letter.id, store.reviews);
                        return (
                          <div key={letter.id} className="relative">
                            <div
                              className={
                                mastery === "unseen"
                                  ? "opacity-35"
                                  : "opacity-100"
                              }
                            >
                              <GlyphTile
                                glyph={letter.glyph}
                                size="lg"
                                onClick={() => setOpenLetterId(letter.id)}
                              />
                            </div>
                            {mastery === "strong" && (
                              <span
                                aria-hidden="true"
                                className="absolute bottom-1.5 left-1/2 h-1.5 w-1.5 -translate-x-1/2 rounded-full bg-gold"
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <footer className="mt-16 border-t border-rule pt-4">
          <button
            type="button"
            onClick={() => setRulesOpen((open) => !open)}
            className="font-latin text-sm text-ink-soft underline transition-colors duration-150 hover:text-sindoor"
          >
            {rulesOpen ? (
              <DualLabel
                variant="inline"
                en="Hide writing order"
                hi="लेखन क्रम छुपाएँ"
              />
            ) : (
              <DualLabel
                variant="inline"
                en="General writing-order rules"
                hi="लेखन क्रम के सामान्य नियम"
              />
            )}
          </button>
          {rulesOpen && (
            <p
              lang="en"
              className="mt-4 max-w-prose font-latin text-sm text-ink-soft"
            >
              {strokeOrderRules.summaryEn}
            </p>
          )}
        </footer>
      </section>

      {openLetter !== undefined && (
        <LetterDetail
          letter={openLetter}
          audio={audio}
          onClose={() => setOpenLetterId(null)}
          onPractice={() => handlePractice(openLetter.id)}
        />
      )}
    </main>
  );
}

/** Best exercise kind for a focused single-letter drill, mirroring the assembler's review-block choice. */
function pickPracticeKind(
  letterId: string,
  rand: () => number,
): "sound-to-symbol" | "symbol-to-sound" | "barakhadi" {
  const letter = letters.find((item) => item.id === letterId);
  if (letter?.kind === "matra") return "barakhadi";
  return rand() < 0.5 ? "sound-to-symbol" : "symbol-to-sound";
}
